import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { createNlsMiddleware, keymap, pathmap, userstate } from '@holmescorp/lcc-state';
import { TopicDetails, ParentTree } from '@holmescorp/lcc-state/interfaces';

import { publish } from '@holmescorp/pub-sub';
import { Icon } from '@holmescorp/base-widgets';

import css from '../css/list-view.m.css';
import { ContentContainer, MicroCardButton, MicroProgress } from './index';

export interface MicroListViewProperties {
	/* ** Unique key */
	key?: string;

	/* ** QA label */
	xqa?: string;

	/* ** Whether to use images for the list items */
	useImages?: boolean;

	/* ** The list of topics to be displayed */
	topics: TopicDetails[];

	/* ** Any action taken on a card that triggers a view switch */
	onAction?: (data?: { [key: string]: any }) => void;

	/* ** Additional classes to be applied to the list view */
	additionalClasses?: string[];
}

const factory = create({
	theme,
	userstate,
	ditabookNls: createNlsMiddleware('ditabook')
}).properties<MicroListViewProperties>();

export const MicroListView = factory(({ id, properties, middleware: { theme, userstate, ditabookNls } }) => {
	const { key, xqa, topics = [], onAction, useImages = true, additionalClasses = [] } = properties();
	const fixedCss = theme.classes(css);

	const {
		messages: {
			card_button_label_start = '[ditabook] card_button_label_start',
			card_button_label_start_quiz = '[ditabook] card_button_label_start_quiz',
			card_button_label_middle = '[ditabook] card_button_label_middle',
			card_button_label_finished = '[ditabook] card_button_label_finished',
			assess_progress_threshold_high = '80'
		}
	} = ditabookNls();

	const status: ParentTree = userstate.get(userstate.path('status'));
	const passingScore = parseInt(assess_progress_threshold_high) || 80;

	const listItems = topics.map((topic) => {
		const kids = topic.children?.filter((child) => !('shortdesc' in child)) || [];
		const isTGF = kids.some((child) => 'topicgroup' in child);

		const key = topic.attributes?.key || '';
		const { path, title } = keymap.get(key)!;
		const { view } = pathmap.get(path)!;

		const { data: topicData } = status[key];
		const isreadingstarted = topicData.complete > 0;
		const isreadingcompleted = topicData.complete === topicData.total;
		const rawScore = topicData.percent === 1 ? 100 : topicData.percent * 100;
		const isquizpassed = rawScore >= passingScore;

		const readTime = kids.reduce((acc, child) => {
			const attrKey = 'topicgroup' in child ? 'topicgroup' : 'topicref' in child ? 'topicref' : 'topicset';
			const time = child[attrKey].attributes?.readtime || 0;
			return acc + time;
		}, 0);

		const props = kids.reduce(
			(accum: { totalComplete: number }, child) => {
				const attrKey = 'topicgroup' in child ? 'topicgroup' : 'topicref' in child ? 'topicref' : 'topicset';
				const topicKey = child[attrKey].attributes?.rel || '';
				const { data } = status[topicKey];
				if (data.complete == data.total) {
					accum['totalComplete'] += 1;
				}
				return accum;
			},
			{ totalComplete: 0 }
		);

		const { totalComplete } = props;

		const leading = v('img', {
			alt: title,
			...formatAriaProperties({ label: title }),
			title: title,
			src: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/icons/icon_${key}.svg`
		});

		const center = v('div', { classes: [fixedCss.listCenter] }, [
			v('div', { classes: [fixedCss.listItemTitle] }, [
				v('div', { classes: [fixedCss.listItemTitleText], xqa: 'progress-area-title' }, [title]),
				v('div', { xqa: 'estimated-read-time' }, [`${readTime} minutes`])
			]),
			v('div', { classes: [] }, [
				w(MicroProgress, { label: 'Reading Progress', value: totalComplete, min: 0, max: kids.length })
			])
		]);

		const trailing = v('div', { classes: [fixedCss.actions] }, [
			w(MicroCardButton, {
				key: `topicgroup-reading-action-${key}`,
				labelText: isreadingstarted
					? isreadingcompleted
						? card_button_label_finished
						: card_button_label_middle
					: card_button_label_start,
				showIcon: true,
				darkMode: !isreadingcompleted,
				icon: isreadingcompleted ? w(Icon, { faname: 'rotate-left' }) : null,
				onClick: (): void => {
					onAction && onAction();
					view();
				},
				onDown: (): void => {
					onAction && onAction();
					view();
				}
			}),
			isTGF
				? w(MicroCardButton, {
						labelText: card_button_label_start_quiz,
						showIcon: false,
						darkMode: isreadingcompleted && !isquizpassed,
						onClick: () => {
							const { path } = keymap.get(key)!;
							publish(`site/quiz`, path, key);
						},
						onDown: () => {
							const { path } = keymap.get(key)!;
							publish(`site/quiz`, path, key);
						}
				  })
				: null
		]);

		return v('div', { role: 'listitem', classes: [fixedCss.listItem] }, [
			useImages ? leading : null,
			center,
			trailing
		]);
	});

	return w(ContentContainer, { id, key, xqa, additionalClasses }, [
		topics ? v('div', { role: 'list', classes: [theme.variant(), fixedCss.list] }, listItems) : null
	]);
});

export default MicroListView;
